import { Modal, Spin } from 'antd';
import { useFormik } from 'formik';
import { observer } from 'mobx-react';
import React from 'react';
import { useParams } from 'react-router-dom';
import { SaveButton } from '../../components/action-buttons';
import { Button } from '../../components/button';
import { NotificationManager } from '../../components/notification-manager';
import { PageTopBar } from '../../components/page-top-bar';
import { Tag } from '../../components/tag';
import { TopTabs } from '../../components/top-tabs';
import { useBusinessUnitStore, useContestStore } from '../../contexts/mobx';
import { useNavigation } from '../../utils/use-navigation';
import { useSingleFlight } from '../../utils/use-single-flight';
import { ContestStateTag } from '../contests-page/contest-state-tag';
import { ContentTab } from './content-tab';
import {
    contentFormSchema,
    ContentFormValues,
    EMPTY_CONTENT_VALUES,
    toContent,
    toContentFormValues,
} from './content-tab/form';
import { EntriesTab } from './entries-tab';
import { GeneralTab } from './general-tab';
import { PagesTab } from './pages-tab';
import { PoliciesTab } from './policies-tab';
import {
    describeVersionChange,
    PoliciesFormValues,
    PolicyVersionChange,
    toPolicies,
    toPreviewPolicies,
    validatePolicies,
    versionChanges,
} from './policies-tab/form';
import { usePoliciesForm } from './policies-tab/use-policies-form';
import { ContestSidebar, SidebarView } from './sidebar';
import { StatsTab } from './stats-tab';
import { ThemeTab } from './theme-tab';
import {
    themeFormSchema,
    ThemeFormValues,
    toTheme,
    toThemeFormValues,
} from './theme-tab/form';
import {
    generalFormSchema,
    GeneralFormValues,
    NEW_CONTEST_VALUES,
    toFormValues,
    toGeneral,
} from './general-tab/form';
import style from './style.module.scss';

interface Params {
    action: 'create' | 'edit';
    id?: string;
    tab?: string;
}

const GENERAL_TAB = 'general';
const CONTENT_TAB = 'content';
const THEME_TAB = 'theme';
const POLICIES_TAB = 'policies';

/** The tabs that edit the contest, which show its checklist and preview beside them. */
const SIDEBAR_TABS: ReadonlySet<string> = new Set([
    GENERAL_TAB,
    CONTENT_TAB,
    THEME_TAB,
    'pages',
    POLICIES_TAB,
]);

/**
 * The General tab opens the sidebar on the checklist; the tabs that shape
 * what a participant sees open it on the preview.
 */
function sidebarViewOf(tab: string): SidebarView {
    return tab === GENERAL_TAB ? 'checklist' : 'preview';
}

/** The screens between Theme and Policies, each a tab named in the address. */
const MIDDLE_TABS = [
    { key: 'pages', name: 'Pages & blocks', children: <PagesTab /> },
];

/** The screens after Policies, which read the contest without editing it. */
const LAST_TABS = [
    { key: 'entries', name: 'Entries', children: <EntriesTab /> },
    { key: 'stats', name: 'Statistics', children: <StatsTab /> },
];

/** The addresses of the policy the preview opens on, in each language. */
function policyPageOf(
    policies: ReturnType<typeof toPreviewPolicies>,
    id: number | undefined
): Partial<Record<'fr' | 'en', string>> | null {
    const draft = policies.find((policy) => policy.id === id);
    return draft
        ? {
              fr: draft.translations.fr?.urlSegment || undefined,
              en: draft.translations.en?.urlSegment || undefined,
          }
        : null;
}

/** Asks before a save gives saved policies a new version. */
function confirmVersions(changes: PolicyVersionChange[]): Promise<boolean> {
    return new Promise((resolve) => {
        Modal.confirm({
            title:
                changes.length === 1
                    ? 'Publish a new version of this policy?'
                    : 'Publish a new version of these policies?',
            content: (
                <ul>
                    {changes.map((change) => (
                        <li key={change.id}>{describeVersionChange(change)}</li>
                    ))}
                </ul>
            ),
            okText: 'Save and publish',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });
}

export const ContestPage = observer(() => {
    const params = useParams<Params>();
    const navigation = useNavigation();
    const store = useContestStore();
    const businessUnitStore = useBusinessUnitStore();
    // The policy whose page the preview shows, by its id in the preview, and
    // how many times one was asked for, which brings the preview to the front.
    const [policyPreview, setPolicyPreview] = React.useState<{
        id: number;
        requests: number;
    } | null>(null);
    const id = params.action === 'edit' ? Number(params.id) : undefined;
    const isCreation = id === undefined;
    // A contest being created has no address yet to name the tab in.
    const [creationTab, setCreationTab] = React.useState(GENERAL_TAB);

    React.useEffect(() => {
        if (id !== undefined) {
            store.fetchContest(id);
        }
    }, [id, store]);

    const contest =
        id !== undefined && store.contest?.id === id ? store.contest : null;

    // The checklist reads the contest as saved, so each read of it runs again.
    React.useEffect(() => {
        if (contest && !contest.isActive) {
            store.fetchPublication(contest.id);
        }
    }, [contest, store]);

    // No form saves on its own: the save sends every tab at once.
    const contentFormik = useFormik<ContentFormValues>({
        initialValues: contest
            ? toContentFormValues(contest)
            : EMPTY_CONTENT_VALUES,
        enableReinitialize: true,
        validationSchema: contentFormSchema(),
        onSubmit: () => undefined,
    });

    const themeFormik = useFormik<ThemeFormValues>({
        initialValues: toThemeFormValues(contest?.theme ?? null),
        enableReinitialize: true,
        validationSchema: themeFormSchema(),
        onSubmit: () => undefined,
    });

    const formik = useFormik<GeneralFormValues>({
        initialValues: contest ? toFormValues(contest) : NEW_CONTEST_VALUES,
        enableReinitialize: true,
        validationSchema: generalFormSchema(),
        onSubmit: () => undefined,
    });

    // A date is read in the time zone the General tab holds, saved or not.
    const {
        formik: policiesFormik,
        afterSave: afterPoliciesSave,
    } = usePoliciesForm(contest, (values) =>
        validatePolicies(values, contest, formik.values.timeZone)
    );

    // The preview names provinces, which the business units list.
    React.useEffect(() => {
        businessUnitStore.fetchBusinessUnits();
    }, [businessUnitStore]);
    const provinceNames = new Map(
        (businessUnitStore.businessUnits ?? []).flatMap((unit) =>
            unit.provinces.map(
                (province) => [province.id, province.name] as const
            )
        )
    );
    const contestProvinceIds = formik.values.provinces.map(
        (province) => province.provinceId
    );
    const previewPolicies = toPreviewPolicies(
        policiesFormik.values,
        { namesById: provinceNames, contestProvinceIds },
        formik.values.timeZone
    );
    const previewProvinces = contestProvinceIds.flatMap((provinceId) => {
        const name = provinceNames.get(provinceId);
        return name ? [name] : [];
    });

    // A contest without a theme keeps the web application's own look until
    // its Theme tab is changed: it is then saved and previewed.
    const themeOfForm =
        contest?.theme || themeFormik.dirty
            ? toTheme(themeFormik.values)
            : null;

    const openTab = (tab: string) => {
        if (isCreation) {
            setCreationTab(tab);
        } else {
            navigation.replaceToContestEdit(
                id,
                tab === GENERAL_TAB ? undefined : tab
            );
        }
    };

    /**
     * Saves every tab in one request, once no file is still uploading.
     * Each form counts it as a submission, so its tab shows its errors, and
     * the first tab with one opens. A new version of a saved policy waits
     * for a confirmation.
     */
    const save = useSingleFlight(async () => {
        if (store.pendingUploads > 0) {
            return;
        }
        await Promise.all([
            formik.submitForm(),
            contentFormik.submitForm(),
            themeFormik.submitForm(),
            policiesFormik.submitForm(),
        ]);
        const [
            generalErrors,
            contentErrors,
            themeErrors,
            policiesErrors,
        ] = await Promise.all([
            formik.validateForm(),
            contentFormik.validateForm(),
            themeFormik.validateForm(),
            policiesFormik.validateForm(),
        ]);
        if (Object.keys(generalErrors).length > 0) {
            openTab(GENERAL_TAB);
            return;
        }
        if (Object.keys(contentErrors).length > 0) {
            openTab(CONTENT_TAB);
            return;
        }
        if (Object.keys(themeErrors).length > 0) {
            openTab(THEME_TAB);
            return;
        }
        if (Object.keys(policiesErrors).length > 0) {
            openTab(POLICIES_TAB);
            return;
        }
        const changes = contest
            ? versionChanges(policiesFormik.values, contest)
            : [];
        if (changes.length > 0 && !(await confirmVersions(changes))) {
            return;
        }
        const general = toGeneral(formik.values);
        const submittedPolicies = policiesFormik.values;
        const saved = {
            general,
            content: toContent(contentFormik.values),
            theme: themeOfForm ?? undefined,
            policies: toPolicies(submittedPolicies, general.timeZone),
        };
        if (isCreation) {
            const createdId = await store.createContest(saved);
            if (createdId !== null) {
                NotificationManager.showSuccess('Contest created');
                navigation.replaceToContestEdit(createdId);
            }
        } else if (await store.updateContest(id, saved)) {
            // A saved date becomes a version, and the form asks for none, so
            // a save that only names dates still starts the form over.
            afterPoliciesSave(
                submittedPolicies,
                !store.contestFetchFailed && store.contest?.id === id
                    ? store.contest
                    : null
            );
            NotificationManager.showSuccess('Contest saved');
        }
    });

    // Enter in a field saves, as the button does.
    const onFormSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        void save();
    };

    const hasUnsavedChanges =
        formik.dirty ||
        contentFormik.dirty ||
        themeFormik.dirty ||
        policiesFormik.dirty;
    const canPublish =
        contest !== null &&
        !hasUnsavedChanges &&
        !store.publicationFetchFailed &&
        store.publication?.contestId === contest.id &&
        store.publication.rules.every((rule) => rule.ok);

    const confirmPublication = (contestId: number) =>
        Modal.confirm({
            title: 'Publish this contest?',
            content:
                'It is served at its address and listed in the applications. Its addresses can no longer change.',
            okText: 'Publish',
            onOk: async () => {
                if (await store.publishContest(contestId)) {
                    NotificationManager.showSuccess('Contest published');
                }
            },
        });

    const englishTitle = contest?.translations.find(
        (translation) => translation.lang === 'en'
    )?.title;

    const title = isCreation ? (
        'New contest'
    ) : (
        <span className={style.title}>
            {englishTitle || 'Contest'}
            <Tag variant="grey">#{id}</Tag>
            {contest && <ContestStateTag state={contest.state} />}
        </span>
    );

    const activeTab = isCreation ? creationTab : params.tab ?? GENERAL_TAB;

    // Nothing to show until the contest is loaded, or once it failed to.
    const pending =
        !isCreation && !contest ? (
            store.contestFetchFailed ? (
                <p>This contest could not be loaded.</p>
            ) : (
                <div className={style.loading}>
                    <Spin />
                </div>
            )
        ) : null;

    const tabs = [
        {
            key: GENERAL_TAB,
            name: 'General',
            children: pending ?? (
                <form onSubmit={onFormSubmit}>
                    <GeneralTab formik={formik} contest={contest} />
                </form>
            ),
        },
        {
            key: CONTENT_TAB,
            name: 'Content',
            children: pending ?? (
                <form onSubmit={onFormSubmit}>
                    <ContentTab formik={contentFormik} />
                </form>
            ),
        },
        {
            key: THEME_TAB,
            name: 'Theme',
            children: pending ?? (
                <form onSubmit={onFormSubmit}>
                    <ThemeTab formik={themeFormik} />
                </form>
            ),
        },
        ...MIDDLE_TABS,
        {
            key: POLICIES_TAB,
            name: 'Policies',
            children: pending ?? (
                <PoliciesTab
                    formik={policiesFormik}
                    contest={contest}
                    timeZone={formik.values.timeZone}
                    onPreview={(policyId) =>
                        setPolicyPreview((current) => ({
                            id: policyId,
                            requests: (current?.requests ?? 0) + 1,
                        }))
                    }
                />
            ),
        },
        ...LAST_TABS,
    ];

    return (
        <>
            <PageTopBar
                title={title}
                goBack={{
                    callback: () => navigation.goToContests(),
                    text: 'Back to list',
                }}
            >
                {contest && !contest.isActive && (
                    <Button
                        variant="secondary"
                        onClick={() => confirmPublication(contest.id)}
                        disabled={!canPublish}
                    >
                        Publish
                    </Button>
                )}
                <SaveButton
                    onClick={save}
                    loading={store.isSaving}
                    disabled={
                        (!isCreation && !contest) || store.pendingUploads > 0
                    }
                >
                    {isCreation ? 'Create' : 'Save'}
                </SaveButton>
            </PageTopBar>

            <TopTabs
                variant="page-header"
                tabsArray={tabs}
                activeKey={activeTab}
                // A contest being created has nothing saved to check or preview.
                aside={
                    contest ? (
                        <ContestSidebar
                            contest={contest}
                            defaultView={sidebarViewOf(activeTab)}
                            hasUnsavedChanges={hasUnsavedChanges}
                            theme={themeOfForm}
                            policies={previewPolicies}
                            provinces={previewProvinces}
                            policyPage={policyPageOf(
                                previewPolicies,
                                policyPreview?.id
                            )}
                            previewRequests={policyPreview?.requests ?? 0}
                            onOpenTab={openTab}
                        />
                    ) : undefined
                }
                isAsideHidden={!SIDEBAR_TABS.has(activeTab)}
                onChange={openTab}
            />
        </>
    );
});
